import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CategorizerService } from '@app/services/categorizer.service';
import { TealiumService } from '@app/services/tealium.service';
import { WindowMessagesService } from '@app/services/window-messages.service';
import { ConfigService } from '@ng-darwin/config';

@Component({
  standalone: false,
  selector: 'app-ag-entities-advise-modal',
  templateUrl: './ag-entities-advise-modal.component.html',
  styleUrls: ['./ag-entities-advise-modal.component.scss'],
})
/**
 * The `AgEntitiesAdviseModalComponent` is a modal component designed to handle
 * the advising of entities within the application. It provides functionality
 * for managing the categorization process, interacting with services, and
 * handling user actions such as closing the modal or adding more entities.
 *
 */
export class AgEntitiesAdviseModalComponent implements OnInit {
  @Input() primary = true;
  expedientCode = '';
  private _endPoints: any;
  public intervalFinish: any;
  public isFinishing = false;

  constructor(
    public dialogRef: MatDialogRef<AgEntitiesAdviseModalComponent>,
    private readonly _windowMessagesService: WindowMessagesService,
    private readonly _categorizerService: CategorizerService,
    private readonly _configService: ConfigService,
    private readonly _tealiumService: TealiumService,
    @Inject(MAT_DIALOG_DATA) private _data: any,
  ) {
    const appConfig = this._configService.config.app || {};
    this._endPoints = appConfig['endPoints'] || {};
    this.expedientCode = sessionStorage.getItem('prop-unique-id') as string;
  }

  ngOnInit(): void {
    this.primary = this._data?.primary;
  }

  addMoreEntities(): void {
    this.dialogRef.close({ action: 'addMore' });
  }

  /**
   * Completes the categorization process by sending the expedient code to the categorizer service.
   * Depending on the response, it either checks if the process is finished or logs an error using the Tealium service.
   *
   * @remarks
   * - If the response status is 202, it invokes `checkIfProcessIsFinished` with the process ID.
   * - If the response status is not 202, it logs a warning error with a 400 error code.
   * - If the request fails, it logs an error with a 500 error code.
   *
   * @returns {void} This method does not return a value.
   */
  finish(): void {
    if (this.isFinishing) {
      return;
    }
    this.isFinishing = true;
    this._categorizerService.putEntities(this.expedientCode).then(
      (response) => {
        if (response.status === 202) {
          this.checkIfProcessIsFinished(response.body.processId);
        } else {
          this.isFinishing = false;
          this._tealiumService.sendError({
            category: 'Finish error',
            action: 'warning',
            data: {
              cod_error: 400,
              desc_error: this._endPoints.categorizer,
            },
          });
        }
      },
      () => {
        this.isFinishing = false;
        this._tealiumService.sendError({
          category: 'Finish error',
          action: 'error',
          data: {
            cod_error: 500,
            desc_error: this._endPoints.categorizer,
          },
        });
      },
    );
  }

  /**
   * Closes the modal dialog and performs cleanup actions.
   *
   * This method removes specific items from the session storage:
   * - 'addMoreBanks': Indicates whether more banks were added.
   * - 'selectEntitiesBanks': Stores selected entities or banks.
   *
   * After clearing the session storage, it closes the dialog reference.
   */
  closeModal(): void {
    sessionStorage.removeItem('addMoreBanks');
    sessionStorage.removeItem('selectEntitiesBanks');
    this.dialogRef.close();
  }

  /**
   * Periodically checks the status of a process by its ID until it is finished or a maximum number of attempts is reached.
   *
   * @param processId - The unique identifier of the process to check.
   *
   * @remarks
   * - This method uses `setInterval` to poll the process status every second.
   * - The polling stops after 30 attempts or when the process is determined to be finished.
   * - The `getProcessStatus` method is called on each interval to retrieve the current status of the process.
   * - The interval is cleared using `clearInterval` once the maximum number of attempts is reached.
   */
  checkIfProcessIsFinished(processId: string): void {
    let numberOfCalls = 0;
    this.intervalFinish = setInterval(() => {
      numberOfCalls += 1;
      if (numberOfCalls > 30) {
        clearInterval(this.intervalFinish);
      } else {
        this.getProcessStatus(processId);
      }
    }, 1000);
  }

  /**
   * Retrieves the status of a process by its ID and handles the process completion logic.
   *
   * @param processId - The unique identifier of the process to check the status for.
   *
   * @remarks
   * This method uses the `_categorizerService` to fetch the process status. If the status
   * is not 'pending', it clears the interval, marks the process as finished, sends a Tealium
   * event, closes the modal, and ends the window messages service. In case of an error during
   * the status retrieval, the interval is cleared.
   *
   * @throws This method does not explicitly throw errors but handles them by clearing the interval.
   */
  getProcessStatus(processId: string): void {
    this._categorizerService
      .getProcessStatus(processId)
      .then((status) => {
        if (
          (status.body.processStatus as string)?.toLowerCase() !== 'pending'
        ) {
          clearInterval(this.intervalFinish);
          this._categorizerService.isProcessFinished = true;
          this._tealiumService.sendEvent({
            category: 'PSD2 Agregador',
            action: 'end_process',
            data: {
              product_name: 'crédito digital',
              product_category: 'PSD2',
            },
          });
          if (this._categorizerService.isProcessFinished) {
            this.closeModal();
            this._windowMessagesService.end();
          }
        }
      })
      .catch(() => {
        clearInterval(this.intervalFinish);
      });
  }
}
